import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getGetStarteds, deleteGetStarted } from '../../actions/getStarted';
import { 
  List, 
  Plus, 
  Trash2, 
  Edit, 
  Eye, 
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const GetStarteds = ({ 
  getGetStarteds, 
  deleteGetStarted, 
  getStarted: { getStarteds, loading }
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredGetStarteds, setFilteredGetStarteds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    getGetStarteds();
  }, [getGetStarteds]);

  useEffect(() => {
    if (getStarteds) {
      setFilteredGetStarteds(
        getStarteds.filter(item => 
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.serviceType.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [getStarteds, searchTerm]);

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      deleteGetStarted(itemToDelete._id);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const getServiceTypeIcon = (serviceType) => {
    switch (serviceType) {
      case 'vending-machine':
        return <i className="fas fa-box me-2 text-primary"></i>;
      case 'delivery':
        return <i className="fas fa-truck me-2 text-success"></i>;
      case 'pod':
        return <i className="fas fa-store me-2 text-info"></i>;
      default:
        return <i className="fas fa-question-circle me-2 text-secondary"></i>;
    }
  };



  return (
    <Fragment>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-0 text-gray-800 d-flex align-items-center">
              <List size={28} className="me-3 text-primary" />
              Get Started Items
            </h1>
            <p className="text-muted">
              Manage onboarding content for different service types
            </p>
          </div>
          <Link to="/inventory/v1.2/getstarted/new" className="btn btn-primary d-flex align-items-center">
            <Plus size={18} className="me-2" />
            Add New Item
          </Link>
        </div>

        {/* Search Bar */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="input-group">
              <span className="input-group-text bg-light">
                <Search size={18} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search by name or service type..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="row">
            {filteredGetStarteds.length > 0 ? (
              filteredGetStarteds.map(item => (
                <div key={item._id} className="col-lg-6 col-xl-4 mb-4">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 d-flex align-items-center">
                        {getServiceTypeIcon(item.serviceType)}
                        {item.name}
                      </h5>
                      <span className={`badge ${item.active ? 'bg-success' : 'bg-secondary'}`}>
                        {item.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <p className="text-muted mb-1">Service Type:</p>
                        <p className="fw-bold mb-0">{item.serviceType}</p>
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-2">Content Steps:</p>
                        {item.content && item.content.length > 0 ? (
                          <div className="list-group list-group-flush">
                            {item.content
                              .sort((a, b) => a.step - b.step)
                              .map((step, index) => (
                                <div key={index} className="list-group-item px-0 py-2">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                      <div className="fw-bold text-sm">Step {step.step}: {step.title}</div>
                                      <small className="text-muted d-block text-truncate" style={{ maxWidth: '200px' }}>
                                        {step.description}
                                      </small>
                                      <small className="text-info">
                                        Action: {step.action || 'No action'}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-muted">No content steps added</p>
                        )}
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-1">Header:</p>
                        <p className="fw-bold mb-0">{item.headerText || 'How it works?'}</p>
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-1">Footer Button:</p>
                        <p className="fw-bold mb-0">
                          {item.footerButton?.text || 'Got It'} 
                          <small className="text-muted ms-2">({item.footerButton?.action || 'close'})</small>
                        </p>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="me-2">Status:</span>
                        {item.active ? (
                          <span className="d-flex align-items-center text-success">
                            <CheckCircle size={16} className="me-1" />
                            Active
                          </span>
                        ) : (
                          <span className="d-flex align-items-center text-secondary">
                            <XCircle size={16} className="me-1" />
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="card-footer bg-white d-flex justify-content-between">
                      <Link
                        to={`/inventory/v1.2/getstarted/${item._id}`}
                        className="btn btn-sm btn-outline-primary d-flex align-items-center"
                      >
                        <Eye size={14} className="me-1" />
                        View
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(item)}
                        className="btn btn-sm btn-outline-danger d-flex align-items-center"
                      >
                        <Trash2 size={14} className="me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="alert alert-info">
                  No GetStarted items found. {searchTerm && 'Try adjusting your search.'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0">
                  <h5 className="modal-title d-flex align-items-center text-danger">
                    <AlertTriangle size={24} className="me-2" />
                    Confirm Delete
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={cancelDelete}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <div className="mb-3">
                      <Trash2 size={48} className="text-danger" />
                    </div>
                    <h6 className="mb-3">Delete "{itemToDelete?.name}"?</h6>
                    <p className="text-muted mb-4">
                      This action cannot be undone. All associated data including assets and configurations will be permanently deleted.
                    </p>
                    <div className="alert alert-warning d-flex align-items-center">
                      <AlertTriangle size={20} className="me-2" />
                      <small>
                        <strong>Warning:</strong> This will permanently delete this item and all its data.
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelDelete}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDelete}
                  >
                    <Trash2 size={16} className="me-2" />
                    Delete Item
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

GetStarteds.propTypes = {
  getGetStarteds: PropTypes.func.isRequired,
  deleteGetStarted: PropTypes.func.isRequired,
  getStarted: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  getStarted: state.getStarted
});

export default connect(
  mapStateToProps,
  { getGetStarteds, deleteGetStarted }
)(GetStarteds); 